import React from "react";
import BackButton from "../../../components/BackButton/BackButton";
import {
  createPullPlan,
  deletePullPlan,
  listPullPlans,
  previewSectionPublish,
  publishSection,
} from "../../../api/clientV2";
import type {
  DepGraph,
  PublishPreview,
  PullPlanEntryMode,
  PullPlanMode,
  PullPlanSectionContext,
  PullPlanSummary,
  TaktColorEntry,
} from "../../../models";
import Spinner from "../../../components/Spinner/Spinner";
import { apiErrorMessage } from "../../../utils/apiError";
import { lvlWord, actWord } from "../../../utils/plural";
import PullPlanGrid, { type AnchorPublishStats } from "../PullPlanGrid/PullPlanGrid";
import type { LedgerSnapshot } from "../ScopeLedger/ScopeLedger";
import PublishSectionModal from "../PublishSectionModal/PublishSectionModal";
import DiscardDraftModal from "./DiscardDraftModal/DiscardDraftModal";
import "./SectionView.css";

interface Props {
  scheduleId: number;
  sectionContext: PullPlanSectionContext;
  /** Single WBS id that anchors this section in the pull_plans table. For
   * TAKT-section pulls (where the operational scope spans multiple WBS
   * nodes), the caller passes the canonical representative WBS id. */
  sectionAnchorWbsId: number;
  /** "plan": open as a pull plan; create a draft on mount if no initialDraftId.
   *  "browse" (default): open read-only (e.g. viewing a past pull). */
  entryMode?: PullPlanEntryMode;
  /** The adjust/repull fork stamped into a draft this view auto-creates. A
   *  SectionView-created draft is always an in-place edit, so this defaults to
   *  "adjust" — re-pull drafts are created with their mode upstream (through
   *  the entry-flow gate), never here. */
  pullMode?: PullPlanMode;
  /** Back to the sections list. Rendered in the topbar. */
  onBack?: () => void;
  onViewHistory: () => void;
  /** Called after a successful Discard — the parent should pop back to the
   *  Pull-By-Takt sections list. Optional so callers that don't navigate
   *  away (legacy) can omit it. */
  onAfterDiscard?: () => void;
  onReloadContext: () => void;
  /** Takt_SectionName for the unified dep-editor graph. Forwarded to the grid;
   *  undefined for non-Takt-section pulls (Edit Dependencies stays hidden). */
  depEditorSection?: string;
  /** When set (and that pull plan exists with status='draft' in this section),
   * SectionView opens directly in Plan mode with that draft already loaded.
   * Used by the Pull History tab's "click a draft row" path and by the
   * Sections tab's "Continue Pull" button. Once consumed, the parent should
   * clear it via `onInitialDraftConsumed`. */
  initialDraftId?: number | null;
  onInitialDraftConsumed?: () => void;
}

const SectionView: React.FC<Props> = ({
  scheduleId,
  sectionContext,
  sectionAnchorWbsId,
  entryMode = "browse",
  pullMode = "adjust",
  onBack,
  onViewHistory,
  onAfterDiscard,
  onReloadContext,
  depEditorSection,
  initialDraftId = null,
  onInitialDraftConsumed,
}) => {
  // Latch: we auto-create a draft at most once per mount when the caller
  // opened this section with intent="Start Pull Plan" (entryMode==="plan"
  // and no initialDraftId). Reset when the section anchor changes so opening
  // a different section can auto-start again.
  const autoStartFiredRef = React.useRef(false);
  React.useEffect(() => {
    autoStartFiredRef.current = false;
  }, [sectionAnchorWbsId]);
  const [pullPlans, setPullPlans] = React.useState<PullPlanSummary[]>([]);
  const [loadingList, setLoadingList] = React.useState(true);
  const [listError, setListError] = React.useState<string | null>(null);
  const [starting, setStarting] = React.useState(false);
  const [discarding, setDiscarding] = React.useState(false);
  const [discardModalOpen, setDiscardModalOpen] = React.useState(false);
  // Single source of truth: non-null = Plan mode editing that draft;
  // null = Browse mode.
  const [activeDraftId, setActiveDraftId] = React.useState<number | null>(null);
  const inPlanMode = activeDraftId !== null;

  // Publish flow: preview the whole draft → confirm in PublishSectionModal →
  // commit. cascade label maps are built from the section context (external
  // downstream activities degrade to "?" / "Other", which is acceptable).
  const [publishPreview, setPublishPreview] = React.useState<PublishPreview | null>(null);
  // The board's scope-ledger snapshot, lifted continuously from the grid into a
  // ref (no re-render) and frozen into state when the publish modal opens so the
  // Diff tab renders exactly what the board's bottom ledger shows.
  const ledgerSnapRef = React.useRef<LedgerSnapshot | null>(null);
  const captureLedger = React.useCallback((snap: LedgerSnapshot) => {
    ledgerSnapRef.current = snap;
  }, []);
  const [publishLedger, setPublishLedger] = React.useState<LedgerSnapshot | null>(null);
  const [anchorStats, setAnchorStats] = React.useState<AnchorPublishStats>({
    total: 0,
    unreviewed: 0,
    severed: [],
  });
  const [anchorGraph, setAnchorGraph] = React.useState<DepGraph | null>(null);
  const [viewSignal, setViewSignal] = React.useState<{
    view: "board" | "sequence" | "anchors";
    nonce: number;
  } | null>(null);
  const [previewing, setPreviewing] = React.useState(false);
  const [publishing, setPublishing] = React.useState(false);
  // Publish modal's Changes-only / Show-all row filter (Changes-only default).
  const [publishChangesOnly, setPublishChangesOnly] = React.useState(true);

  const cascadeMaps = React.useMemo(() => {
    const activityWorkCodeMap = new Map<number, string>();
    const zoneMap = new Map<number, string>();
    for (const a of sectionContext.activities) {
      if (a.takt_work) activityWorkCodeMap.set(a.id, a.takt_work);
      zoneMap.set(a.id, a.takt_b || "Other");
    }
    const colors: Record<string, TaktColorEntry> = {};
    for (const [wc, fill] of Object.entries(sectionContext.takt_work_colors)) {
      colors[wc] = { fill, font: "#fff" };
    }
    return { activityWorkCodeMap, zoneMap, colors };
  }, [sectionContext]);

  const reloadPullPlans = React.useCallback(async () => {
    setLoadingList(true);
    setListError(null);
    try {
      const list = await listPullPlans(scheduleId, {
        section_wbs_id: sectionAnchorWbsId,
      });
      setPullPlans(list);
    } catch (e: unknown) {
      setListError(apiErrorMessage(e, "Failed to load pull plans."));
    } finally {
      setLoadingList(false);
    }
  }, [scheduleId, sectionAnchorWbsId]);

  React.useEffect(() => {
    setActiveDraftId(null);
    reloadPullPlans();
  }, [reloadPullPlans]);

  const handleOpenPublish = React.useCallback(async () => {
    if (activeDraftId == null) return;
    setPreviewing(true);
    setListError(null);
    try {
      const preview = await previewSectionPublish(scheduleId, activeDraftId);
      setPublishLedger(ledgerSnapRef.current);
      setPublishChangesOnly(true);
      setPublishPreview(preview);
    } catch (e: unknown) {
      setListError(apiErrorMessage(e, "Failed to preview publish."));
    } finally {
      setPreviewing(false);
    }
  }, [scheduleId, activeDraftId]);

  // Close the review back to the board (the "← Plan" back + Cancel both do this).
  const closePublish = React.useCallback(() => {
    setPublishPreview(null);
    setPublishLedger(null);
  }, []);

  const handleConfirmPublish = React.useCallback(async (applyDownstream: boolean) => {
    if (activeDraftId == null) return;
    setPublishing(true);
    setListError(null);
    try {
      await publishSection(scheduleId, activeDraftId, { applyDownstream });
      setPublishPreview(null);
      setPublishLedger(null);
      setActiveDraftId(null);
      onReloadContext();
      if (onAfterDiscard) {
        onAfterDiscard();
      } else {
        await reloadPullPlans();
      }
    } catch (e: unknown) {
      setListError(apiErrorMessage(e, "Failed to publish."));
    } finally {
      setPublishing(false);
    }
  }, [scheduleId, activeDraftId, onReloadContext, onAfterDiscard, reloadPullPlans]);

  // When the user opened this section by clicking a draft row in the Pull
  // History tab, the parent passed initialDraftId. After the list loads,
  // verify the draft exists + is still a draft, then auto-enter Plan mode on
  // it. Consumed once via onInitialDraftConsumed so the parent clears the
  // value (next reopen of this section starts in Browse mode).
  React.useEffect(() => {
    if (initialDraftId == null) return;
    if (loadingList) return;
    const match = pullPlans.find((p) => p.id === initialDraftId);
    if (match && match.status === "draft") {
      setActiveDraftId(match.id);
    }
    onInitialDraftConsumed?.();
  }, [initialDraftId, loadingList, pullPlans, onInitialDraftConsumed]);

  const pastPullCount = React.useMemo(
    () => pullPlans.filter((p) => p.status !== "draft").length,
    [pullPlans],
  );
  // A section holds at most one live draft. Whenever one exists, the section is
  // "in a plan" — there is never a reason to Start a new one, only Continue.
  const existingDraft = React.useMemo(
    () => pullPlans.find((p) => p.status === "draft") ?? null,
    [pullPlans],
  );

  // "N levels · M activities" for the review header — mirrors the board's
  // subtitle (distinct WBS leaves the section spans · its activity count).
  const reviewSubtitle = React.useMemo(() => {
    const leaves = new Set(
      sectionContext.activities
        .map((a) => a.wbs_path[a.wbs_path.length - 1])
        .filter(Boolean),
    );
    const nL = leaves.size;
    const nA = sectionContext.activities.length;
    return `${nL} ${lvlWord(nL)} · ${nA} ${actWord(nA)}`;
  }, [sectionContext.activities]);

  const startPull = React.useCallback(async () => {
    if (existingDraft) {
      setActiveDraftId(existingDraft.id);
      return;
    }
    setStarting(true);
    try {
      const today = new Date().toLocaleDateString(undefined, {
        month: "numeric",
        day: "numeric",
      });
      const created = await createPullPlan(scheduleId, {
        section_wbs_id: sectionAnchorWbsId,
        name: `${sectionContext.section.name} — ${today}`,
        // Persist the Takt section being planned so publish stamps newly-coded
        // work with THIS section — never cribbing a sibling's section from a WBS
        // leaf that two sections share.
        takt_section_name: depEditorSection,
        payload_json: JSON.stringify({ kind: "takt", mode: pullMode }),
      });
      await reloadPullPlans();
      setActiveDraftId(created.id);
    } catch (e: unknown) {
      setListError(apiErrorMessage(e, "Failed to start pull plan."));
    } finally {
      setStarting(false);
    }
  }, [scheduleId, sectionAnchorWbsId, sectionContext.section.name, reloadPullPlans, existingDraft, depEditorSection, pullMode]);

  // Enter Plan mode on mount when the caller asked for it without a seeded
  // draft id (the "Plan" button on the takt board, or "Start Plan" on the
  // Sections tab). Resume this section's existing draft if there is one;
  // only create when none — a section holds at most one draft. Skipped in
  // browse mode and when initialDraftId is pending (Continue Plan takes over).
  React.useEffect(() => {
    if (entryMode !== "plan") return;
    if (initialDraftId != null) return;
    if (loadingList) return;
    if (activeDraftId != null) return;
    if (starting) return;
    if (autoStartFiredRef.current) return;
    autoStartFiredRef.current = true;
    const existingDraft = pullPlans.find((p) => p.status === "draft");
    if (existingDraft) {
      setActiveDraftId(existingDraft.id);
    } else {
      void startPull();
    }
  }, [entryMode, initialDraftId, loadingList, activeDraftId, starting, startPull, pullPlans]);

  const confirmDiscard = async () => {
    if (activeDraftId == null) return;
    setDiscarding(true);
    try {
      await deletePullPlan(scheduleId, activeDraftId);
      setActiveDraftId(null);
      setDiscardModalOpen(false);
      if (onAfterDiscard) {
        onAfterDiscard();
      } else {
        await reloadPullPlans();
      }
    } catch (e: unknown) {
      setListError(apiErrorMessage(e, "Failed to discard."));
    } finally {
      setDiscarding(false);
    }
  };

  // Status + actions for the right side of the workspace's single header row.
  const renderActions = () => {
    if (!inPlanMode) {
      return (
        <>
          {pastPullCount > 0 && (
            <button
              type="button"
              className="section-view-topbar-link"
              onClick={onViewHistory}
            >
              {pastPullCount} past pull{pastPullCount === 1 ? "" : "s"} →
            </button>
          )}
          {existingDraft ? (
            <button
              type="button"
              className="section-view-topbar-cta"
              onClick={() => setActiveDraftId(existingDraft.id)}
              disabled={loadingList}
            >
              Continue Plan
            </button>
          ) : (
            <button
              type="button"
              className="section-view-topbar-cta"
              onClick={startPull}
              disabled={loadingList || starting}
            >
              {starting ? "Starting…" : "Start Plan"}
            </button>
          )}
        </>
      );
    }
    // The right side carries just the primary publish CTA; Discard lives in the
    // title menu ("{name} DRAFT ▾").
    return (
      <button
        type="button"
        className="section-view-topbar-publish"
        onClick={handleOpenPublish}
        disabled={previewing || publishing || discarding}
      >
        {previewing ? "Previewing…" : "Review & publish →"}
      </button>
    );
  };

  return (
    <div className="section-view">
      {listError && <div className="pull-plan-error">{listError}</div>}
      {loadingList && pullPlans.length === 0 && (
        <Spinner visible label="Loading section..." />
      )}
      {!loadingList && !publishPreview && (
        <PullPlanGrid
          context={sectionContext}
          scheduleId={scheduleId}
          draftId={activeDraftId}
          readOnly={!inPlanMode}
          depEditorSection={depEditorSection}
          onAnchorStatsChange={setAnchorStats}
          onDepGraphChange={setAnchorGraph}
          onLedgerSnapshotChange={captureLedger}
          viewSignal={viewSignal}
          headerLeft={onBack ? <BackButton onClick={onBack} label="Sections" /> : undefined}
          headerRight={renderActions()}
          onDiscardDraft={() => setDiscardModalOpen(true)}
        />
      )}
      {discardModalOpen && (
        <DiscardDraftModal
          onCancel={() => setDiscardModalOpen(false)}
          onConfirm={confirmDiscard}
          discarding={discarding}
        />
      )}
      {publishPreview && (
        <PublishSectionModal
          sectionTitle={sectionContext.section.name}
          headerLeft={<BackButton onClick={closePublish} label="Plan" />}
          subtitle={reviewSubtitle}
          anchorStats={anchorStats}
          anchorGraph={anchorGraph}
          onEditAnchors={() => {
            setPublishPreview(null);
            setViewSignal({ view: "anchors", nonce: Date.now() });
          }}
          preview={publishPreview}
          ledger={publishLedger}
          changesOnly={publishChangesOnly}
          onChangesOnlyChange={setPublishChangesOnly}
          colors={cascadeMaps.colors}
          activityWorkCodeMap={cascadeMaps.activityWorkCodeMap}
          zoneMap={cascadeMaps.zoneMap}
          publishing={publishing}
          onPublish={handleConfirmPublish}
          onCancel={closePublish}
        />
      )}
    </div>
  );
};

export default SectionView;
